import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { candidatesAPI, jobsAPI, interviewsAPI } from '../api/client';
import '../pages/AnimationsAndStyles.css';

const PIPELINE_STAGES = [
  { key: 'screening', label: 'Screening', statuses: ['applied', 'screen_pending', 'screen_selected'], color: '#6366F1' },
  { key: 'l1', label: 'L1 Interview', statuses: ['l1_scheduled', 'l1_select'], color: '#0EA5E9' },
  { key: 'l2', label: 'L2 Interview', statuses: ['l2_scheduled', 'l2_select', 'shared_to_client', 'hr_round'], color: '#F59E0B' },
  { key: 'offer', label: 'Offer', statuses: ['col_issued', 'col_accepted', 'fol_issued', 'fol_accepted'], color: '#8B5CF6' },
  { key: 'joined', label: 'Joined', statuses: ['joined'], color: '#10B981' },
  { key: 'rejected', label: 'Rejected', statuses: ['screen_rejected', 'l1_reject', 'l2_reject', 'col_rejected', 'fol_rejected', 'candidate_backed', 'drop', 'cancelled'], color: '#EF4444' },
];

const REJECTED_STATUSES = new Set([
  'screen_rejected', 'l1_reject', 'l2_reject', 'col_rejected',
  'fol_rejected', 'candidate_backed', 'drop', 'cancelled'
]);

const OFFER_STATUSES = new Set(['col_issued', 'col_accepted', 'fol_issued', 'fol_accepted']);

// A joining journey starts once an offer is in play and remains relevant if it
// is accepted, rejected, cancelled, or ends in a completed joining.
const JOINING_PIPELINE_STATUSES = new Set([
  ...OFFER_STATUSES,
  'joined', 'col_rejected', 'fol_rejected', 'candidate_backed', 'drop', 'cancelled',
]);

const ALL_CANDIDATE_STATUSES = [
  'applied', 'screen_pending', 'screen_selected', 'screen_rejected',
  'l1_scheduled', 'l1_select', 'l1_reject',
  'l2_scheduled', 'l2_select', 'l2_reject',
  'shared_to_client', 'hr_round',
  'col_issued', 'col_accepted', 'col_rejected',
  'fol_issued', 'fol_accepted', 'fol_rejected',
  'joined', 'candidate_backed', 'another_profile_requested',
  'cancelled', 'drop', 'hold'
];

const DATE_PRESETS = [
  { key: 'overall', label: 'Overall' },
  { key: 'this_week', label: 'This Week' },
  { key: 'this_month', label: 'This Month' },
  { key: 'last_30', label: 'Last 30 Days' },
  { key: 'last_90', label: 'Last 90 Days' },
  { key: 'specific', label: 'Specific Date' },
  { key: 'custom', label: 'Custom Range' },
];

const INTERVIEW_STATUS_MAP = {
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
  rescheduled: 'Rescheduled',
};

const SCREENING_STATUSES = new Set(['applied', 'screen_pending', 'screen_selected', 'screen_rejected']);
const NEW_CANDIDATE_STATUSES = new Set(['applied', 'screen_pending']);
const SCHEDULED_INTERVIEW_CANDIDATE_STATUSES = new Set(['l1_scheduled', 'l2_scheduled']);
const SHORTLIST_STATUSES = new Set(['l1_select', 'l2_select', 'shared_to_client', 'hr_round']);
const JOINED_STATUSES = new Set(['joined']);

const DONUT_SEGMENTS = {
  candidatesAdded: [
    { key: 'new', label: 'New', color: '#6366F1' },
    { key: 'ongoing', label: 'Ongoing', color: '#3B82F6' },
    { key: 'rejected', label: 'Rejected', color: '#EF4444' },
  ],
  screeningProgress: [
    { key: 'screened', label: 'Screened', color: '#10B981' },
    { key: 'pending', label: 'Pending', color: '#F59E0B' },
    { key: 'rejected', label: 'Rejected', color: '#EF4444' },
  ],
  interviews: [
    { key: 'completed', label: 'Completed', color: '#10B981' },
    { key: 'scheduled', label: 'Scheduled', color: '#3B82F6' },
    { key: 'cancelled', label: 'Cancelled', color: '#EF4444' },
  ],
  joiningProgress: [
    { key: 'joined', label: 'Joined', color: '#10B981' },
    { key: 'pending', label: 'Pending', color: '#F59E0B' },
    { key: 'rejected', label: 'Rejected', color: '#EF4444' },
  ],
};

function getDateRange(preset, specificDate, customStart, customEnd) {
  const now = new Date();
  const startOfDay = (d) => { const r = new Date(d); r.setHours(0, 0, 0, 0); return r; };
  const endOfDay = (d) => { const r = new Date(d); r.setHours(23, 59, 59, 999); return r; };

  switch (preset) {
    case 'overall':
      return null;
    case 'this_week': {
      const s = new Date(now);
      const weekday = now.getDay();
      s.setDate(now.getDate() + (weekday === 0 ? -6 : 1 - weekday));
      s.setHours(0, 0, 0, 0);
      return { start: s, end: endOfDay(now) };
    }
    case 'this_month': {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: s, end: now };
    }
    case 'last_30': {
      const s = new Date(now);
      s.setDate(now.getDate() - 30);
      s.setHours(0, 0, 0, 0);
      return { start: s, end: now };
    }
    case 'last_90': {
      const s = new Date(now);
      s.setDate(now.getDate() - 90);
      s.setHours(0, 0, 0, 0);
      return { start: s, end: now };
    }
    case 'specific': {
      if (!specificDate) return null;
      const d = new Date(specificDate);
      return { start: startOfDay(d), end: endOfDay(d) };
    }
    case 'custom': {
      if (!customStart || !customEnd) return null;
      return { start: startOfDay(new Date(customStart)), end: endOfDay(new Date(customEnd)) };
    }
    default:
      return null;
  }
}

function isDateInRange(dateStr, range) {
  if (!range) return true;
  // A selected period must not pull in records with no date for that event.
  if (!dateStr) return false;
  const d = new Date(dateStr);
  return d >= range.start && d <= range.end;
}

function getDomainFromDept(department) {
  if (!department) return null;
  const idx = department.indexOf(' - ');
  return idx >= 0 ? department.slice(idx + 3).trim() : null;
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  try {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays < 1) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 30) return `${diffDays} days ago`;
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
    const diffYears = Math.floor(diffMonths / 12);
    return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
  } catch {
    return '';
  }
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  } catch {
    return '-';
  }
}

function getStageForStatus(status) {
  return PIPELINE_STAGES.find(s => s.statuses.includes(status)) || null;
}

function statusLabel(s) {
  return (s || 'unknown').replace(/_/g, ' ');
}

export default function JobDashboard() {
  const [allJobs, setAllJobs] = useState([]);
  const [allCandidates, setAllCandidates] = useState([]);
  const [allInterviews, setAllInterviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const [jobsRes, candRes, intRes] = await Promise.all([
          jobsAPI.getAll(),
          candidatesAPI.getAll(),
          interviewsAPI.getAll({ scope: 'global' }),
        ]);
        setAllJobs(Array.isArray(jobsRes.data) ? jobsRes.data : []);
        setAllCandidates(Array.isArray(candRes.data) ? candRes.data : []);
        setAllInterviews(Array.isArray(intRes.data) ? intRes.data : []);
      } catch (err) {
        console.error('Failed to load dashboard data', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const [filters, setFilters] = useState({
    datePreset: 'overall',
    specificDate: '',
    customStart: '',
    customEnd: '',
    department: '',
    location: '',
    jobStatus: '',
  });

  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      datePreset: 'overall',
      specificDate: '',
      customStart: '',
      customEnd: '',
      department: '',
      location: '',
      jobStatus: '',
    });
  }, []);

  const hasActiveFilters = filters.datePreset !== 'overall' ||
    filters.specificDate || filters.customStart || filters.customEnd ||
    filters.department || filters.location || filters.jobStatus;

  const dateRange = useMemo(
    () => getDateRange(filters.datePreset, filters.specificDate, filters.customStart, filters.customEnd),
    [filters.datePreset, filters.specificDate, filters.customStart, filters.customEnd]
  );

  const filterCandidatesByDimensions = useCallback((candidates) => {
    let result = candidates || [];
    if (filters.department) {
      const deptJobIds = new Set(allJobs.filter(j => j.department === filters.department).map(j => j.id));
      result = result.filter(c => deptJobIds.has(c.job_id));
    }
    if (filters.location || filters.jobStatus) {
      const matchingJobIds = new Set(allJobs
        .filter(j => (!filters.location || j.location === filters.location) && (!filters.jobStatus || j.status === filters.jobStatus))
        .map(j => j.id));
      result = result.filter(c => matchingJobIds.has(c.job_id));
    }
    return result;
  }, [allJobs, filters.department, filters.location, filters.jobStatus]);

  // Dimension filters are shared by every activity stream. Date filtering is
  // applied to each stream's actual activity date below.
  const chartCandidates = useMemo(() => filterCandidatesByDimensions(allCandidates), [allCandidates, filterCandidatesByDimensions]);
  const filteredCandidates = useMemo(
    () => dateRange ? chartCandidates.filter(c => isDateInRange(c.created_at, dateRange)) : chartCandidates,
    [chartCandidates, dateRange]
  );

  const filteredInterviews = useMemo(() => {
    let result = allInterviews || [];

    const candidateIds = new Set(chartCandidates.map(c => c.id));
    result = result.filter(i => candidateIds.has(i.candidate_id));
    return dateRange ? result.filter(i => isDateInRange(i.scheduled_date, dateRange)) : result;
  }, [allInterviews, chartCandidates, dateRange]);

  const filteredJobs = useMemo(() => {
    let result = allJobs || [];
    if (filters.department) result = result.filter(j => j.department === filters.department);
    if (filters.location) result = result.filter(j => j.location === filters.location);
    if (filters.jobStatus) result = result.filter(j => j.status === filters.jobStatus);
    return dateRange ? result.filter(j => isDateInRange(j.created_at, dateRange)) : result;
  }, [allJobs, dateRange, filters.department, filters.location, filters.jobStatus]);

  const totalPositions = useMemo(() => filteredJobs.reduce((sum, j) => sum + (j.positions ?? 1), 0), [filteredJobs]);
  const openJobsCount = useMemo(() => filteredJobs.filter(j => j.status === 'open').length, [filteredJobs]);

  const kpis = useMemo(() => {
    const positions = totalPositions;
    const total = filteredCandidates.length;
    const rejected = filteredCandidates.filter(c => REJECTED_STATUSES.has(c.status)).length;
    const joined = filteredCandidates.filter(c => c.status === 'joined').length;
    const inOffer = filteredCandidates.filter(c => OFFER_STATUSES.has(c.status)).length;
    const active = total - rejected - joined;
    const remaining = Math.max(0, positions - joined);
    const filledPct = positions > 0 ? Math.round((joined / positions) * 100) : 0;
    const offerAcceptRate = inOffer > 0
      ? Math.round((filteredCandidates.filter(c => ['col_accepted', 'fol_accepted'].includes(c.status)).length / inOffer) * 100)
      : 0;
    const conversionRate = total > 0 ? Math.round((joined / total) * 100) : 0;
    const scheduledInterviews = filteredInterviews.filter(i => i.status === 'scheduled' || i.status === 'rescheduled').length;

    return { positions, total, rejected, joined, inOffer, active, remaining, filledPct, offerAcceptRate, conversionRate, scheduledInterviews };
  }, [filteredCandidates, filteredInterviews, totalPositions]);

  const stageCounts = useMemo(() => {
    const counts = {};
    PIPELINE_STAGES.forEach(s => { counts[s.key] = 0; });
    filteredCandidates.forEach(c => {
      const stage = getStageForStatus(c.status);
      if (stage) counts[stage.key]++;
    });
    return PIPELINE_STAGES.map(s => ({ ...s, count: counts[s.key] || 0 }));
  }, [filteredCandidates]);

  const pipelineTotal = useMemo(
    () => stageCounts.reduce((sum, s) => sum + s.count, 0),
    [stageCounts]
  );

  const recruiterBreakdown = useMemo(() => {
    const map = {};
    filteredCandidates.forEach(c => {
      const name = c.owner_name || c.assigned_to_name || 'Unassigned';
      if (!map[name]) map[name] = { name, total: 0, joined: 0, rejected: 0, active: 0 };
      map[name].total++;
      if (c.status === 'joined') map[name].joined++;
      else if (REJECTED_STATUSES.has(c.status)) map[name].rejected++;
      else map[name].active++;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filteredCandidates]);

  const statusBreakdown = useMemo(() => {
    const map = {};
    filteredCandidates.forEach(c => {
      const s = c.status || 'unknown';
      map[s] = (map[s] || 0) + 1;
    });
    return ALL_CANDIDATE_STATUSES.map(s => ({
      status: s,
      count: map[s] || 0,
    })).sort((a, b) => b.count - a.count);
  }, [filteredCandidates]);

  const uniqueDepartments = useMemo(() => {
    const set = new Set();
    (allJobs || []).forEach(j => { if (j.department) set.add(j.department); });
    return [...set].sort();
  }, [allJobs]);

  const uniqueLocations = useMemo(() => {
    const set = new Set();
    (allJobs || []).forEach(j => { if (j.location) set.add(j.location); });
    return [...set].sort();
  }, [allJobs]);

  const uniqueJobStatuses = useMemo(() => {
    const set = new Set();
    (allJobs || []).forEach(j => { if (j.status) set.add(j.status); });
    return [...set].sort();
  }, [allJobs]);

  const donutData = useMemo(() => {
    const inRange = date => !dateRange || isDateInRange(date, dateRange);
    const countBy = (records, initial, categoryFor) => {
      const totals = { ...initial };
      records.forEach(record => { totals[categoryFor(record)]++; });
      return totals;
    };
    const candidatesAdded = filteredCandidates;
    const screeningProgress = chartCandidates.filter(candidate =>
      SCREENING_STATUSES.has(candidate.status) && inRange(candidate.updated_at || candidate.created_at)
    );
    const joiningProgress = chartCandidates.filter(candidate =>
      (JOINING_PIPELINE_STATUSES.has(candidate.status) || candidate.joining_date) &&
      inRange(candidate.joining_date || candidate.updated_at || candidate.created_at)
    );
    const interviewCategory = interview => {
      if (interview.status === 'completed') return 'completed';
      if (interview.status === 'cancelled') return 'cancelled';
      return 'scheduled'; // The backend's scheduled and rescheduled values are upcoming interviews.
    };
    const candidateIdsWithScheduledInterview = new Set(filteredInterviews
      .filter(interview => interviewCategory(interview) === 'scheduled')
      .map(interview => interview.candidate_id));
    const shouldIncludeScheduledPipeline = true;
    const scheduledPipelineCandidates = shouldIncludeScheduledPipeline
      ? chartCandidates.filter(candidate =>
        SCHEDULED_INTERVIEW_CANDIDATE_STATUSES.has(candidate.status) &&
        inRange(candidate.updated_at || candidate.created_at) &&
        !candidateIdsWithScheduledInterview.has(candidate.id)
      )
      : [];
    // Add a candidate pipeline signal only when it has no matching scheduled
    // interview record, avoiding a duplicate scheduled slice for the same link.
    const interviewDonutRecords = [
      ...filteredInterviews,
      ...scheduledPipelineCandidates.map(candidate => ({ candidate_id: candidate.id, status: 'scheduled' })),
    ];

    return {
      candidatesAdded: countBy(candidatesAdded, { new: 0, ongoing: 0, rejected: 0 }, candidate => {
        if (REJECTED_STATUSES.has(candidate.status)) return 'rejected';
        return NEW_CANDIDATE_STATUSES.has(candidate.status) ? 'new' : 'ongoing';
      }),
      screeningProgress: countBy(screeningProgress, { screened: 0, pending: 0, rejected: 0 }, candidate => {
        if (candidate.status === 'screen_rejected') return 'rejected';
        return candidate.status === 'screen_selected' ? 'screened' : 'pending';
      }),
      interviews: countBy(interviewDonutRecords, { completed: 0, scheduled: 0, cancelled: 0 }, interviewCategory),
      joiningProgress: countBy(joiningProgress, { joined: 0, pending: 0, rejected: 0 }, candidate => {
        if (candidate.status === 'joined') return 'joined';
        return REJECTED_STATUSES.has(candidate.status) ? 'rejected' : 'pending';
      }),
    };
  }, [chartCandidates, dateRange, filteredCandidates, filteredInterviews]);

  const domainBreakdown = useMemo(() => {
    const map = {};
    (filteredCandidates || []).forEach(c => {
      const domain = getDomainFromDept(c.department);
      if (domain) {
        if (!map[domain]) map[domain] = { name: domain, total: 0, joined: 0, rejected: 0 };
        map[domain].total++;
        if (c.status === 'joined') map[domain].joined++;
        else if (REJECTED_STATUSES.has(c.status)) map[domain].rejected++;
      }
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filteredCandidates]);

  const subDepartmentBreakdown = useMemo(() => {
    const map = {};
    (filteredCandidates || []).forEach(c => {
      const dept = c.department || 'Unspecified';
      if (!map[dept]) map[dept] = { name: dept, total: 0, joined: 0, ongoing: 0, rejected: 0 };
      map[dept].total++;
      if (c.status === 'joined') map[dept].joined++;
      else if (REJECTED_STATUSES.has(c.status)) map[dept].rejected++;
      else map[dept].ongoing++;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filteredCandidates]);

  const locationBreakdown = useMemo(() => {
    const jobLocationMap = {};
    (allJobs || []).forEach(j => {
      if (j.location) jobLocationMap[j.id] = j.location;
    });
    const map = {};
    (filteredCandidates || []).forEach(c => {
      const loc = jobLocationMap[c.job_id] || 'Unknown';
      if (!map[loc]) map[loc] = { name: loc, total: 0, joined: 0, rejected: 0 };
      map[loc].total++;
      if (c.status === 'joined') map[loc].joined++;
      else if (REJECTED_STATUSES.has(c.status)) map[loc].rejected++;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [allJobs, filteredCandidates]);

  if (loading) {
    return (
      <div style={page}>
        <style>{css}</style>
        <div className="jdb-section-card" style={{ textAlign: 'center', padding: 48 }}>
          <p style={{ color: '#64748B', fontSize: 14 }}>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  const maxSubDeptCount = subDepartmentBreakdown.length > 0
    ? Math.max(...subDepartmentBreakdown.map(d => d.total)) : 0;
  const maxDomainCount = domainBreakdown.length > 0
    ? Math.max(...domainBreakdown.map(d => d.total)) : 0;
  const maxLocationCount = locationBreakdown.length > 0
    ? Math.max(...locationBreakdown.map(d => d.total)) : 0;

  return (
    <div style={page}>
      <style>{css}</style>

      {/* DASHBOARD HEADER */}
      <div className="jdb-header">
        <div>
          <p className="jdb-subtitle">Overall analytics across all jobs</p>
        </div>
        {/* <div className="jdb-header-meta">
          <span className="jdb-meta-chip">{filteredJobs.length} Total Jobs</span>
          <span className="jdb-meta-chip">{openJobsCount} Open</span>
          <span className="jdb-meta-chip">{totalPositions} Positions</span>
        </div> */}
      </div>

      {/* SUMMARY INFO ROW */}
      <div className="jdb-info-row">
        <div className="jdb-info-item"><span className="jdb-info-label">Total Jobs</span><span className="jdb-info-val">{filteredJobs.length}</span></div>
        <div className="jdb-info-item"><span className="jdb-info-label">Open Jobs</span><span className="jdb-info-val">{openJobsCount}</span></div>
        <div className="jdb-info-item"><span className="jdb-info-label">Total Positions</span><span className="jdb-info-val">{totalPositions}</span></div>
        <div className="jdb-info-item"><span className="jdb-info-label">Total Candidates</span><span className="jdb-info-val">{kpis.total}</span></div>
        <div className="jdb-info-item"><span className="jdb-info-label">Total Interviews</span><span className="jdb-info-val">{filteredInterviews.length}</span></div>
      </div>

      {/* FILTER BAR */}
      <div className="jdb-filter-bar">
        <div className="jdb-filter-row">
          <div className="jdb-filter-group">
            <label className="jdb-filter-label">Date Range</label>
            <select
              className="jdb-select"
              value={filters.datePreset}
              onChange={e => updateFilter('datePreset', e.target.value)}
            >
              {DATE_PRESETS.map(p => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </div>

          {filters.datePreset === 'specific' && (
            <div className="jdb-filter-group">
              <label className="jdb-filter-label">Select Date</label>
              <input
                type="date"
                className="jdb-input"
                value={filters.specificDate}
                onChange={e => updateFilter('specificDate', e.target.value)}
              />
            </div>
          )}

          {filters.datePreset === 'custom' && (
            <div className="jdb-filter-group jdb-date-range-pair">
              <div>
                <label className="jdb-filter-label">From</label>
                <input
                  type="date"
                  className="jdb-input"
                  value={filters.customStart}
                  onChange={e => updateFilter('customStart', e.target.value)}
                />
              </div>
              <div>
                <label className="jdb-filter-label">To</label>
                <input
                  type="date"
                  className="jdb-input"
                  value={filters.customEnd}
                  onChange={e => updateFilter('customEnd', e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="jdb-filter-group">
            <label className="jdb-filter-label">Department</label>
            <select
              className="jdb-select"
              value={filters.department}
              onChange={e => updateFilter('department', e.target.value)}
            >
              <option value="">All Departments</option>
              {uniqueDepartments.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          <div className="jdb-filter-group">
            <label className="jdb-filter-label">Location</label>
            <select
              className="jdb-select"
              value={filters.location}
              onChange={e => updateFilter('location', e.target.value)}
            >
              <option value="">All Locations</option>
              {uniqueLocations.map(l => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>

          <div className="jdb-filter-group">
            <label className="jdb-filter-label">Job Status</label>
            <select
              className="jdb-select"
              value={filters.jobStatus}
              onChange={e => updateFilter('jobStatus', e.target.value)}
            >
              <option value="">All Job Status</option>
              {uniqueJobStatuses.map(s => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>

          {hasActiveFilters && (
            <button type="button" className="jdb-reset-btn" onClick={resetFilters}>
              Reset Filters
            </button>
          )}
        </div>
      </div>

      {/* KPI GRID */}
      <div className="jdb-kpi-grid">
        <div className="jdb-kpi-card">
          <div className="jdb-kpi-icon" style={{ background: '#EFF6FF', color: '#1E40AF' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
          </div>
          <div className="jdb-kpi-body">
            <span className="jdb-kpi-value">{kpis.positions}</span>
            <span className="jdb-kpi-label">Total Positions</span>
          </div>
        </div>
        <div className="jdb-kpi-card">
          <div className="jdb-kpi-icon" style={{ background: '#F0FDF4', color: '#16A34A' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          </div>
          <div className="jdb-kpi-body">
            <span className="jdb-kpi-value">{kpis.total}</span>
            <span className="jdb-kpi-label">Total Candidates</span>
          </div>
        </div>
        <div className="jdb-kpi-card">
          <div className="jdb-kpi-icon" style={{ background: '#FEF3C7', color: '#D97706' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          </div>
          <div className="jdb-kpi-body">
            <span className="jdb-kpi-value">{kpis.active}</span>
            <span className="jdb-kpi-label">In Pipeline</span>
          </div>
        </div>
        <div className="jdb-kpi-card">
          <div className="jdb-kpi-icon" style={{ background: '#F0FDF4', color: '#059669' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          </div>
          <div className="jdb-kpi-body">
            <span className="jdb-kpi-value" style={{ color: '#059669' }}>{kpis.joined}</span>
            <span className="jdb-kpi-label">Joined ({kpis.filledPct}%)</span>
          </div>
        </div>
        <div className="jdb-kpi-card">
          <div className="jdb-kpi-icon" style={{ background: '#FEF2F2', color: '#DC2626' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
          </div>
          <div className="jdb-kpi-body">
            <span className="jdb-kpi-value" style={{ color: '#DC2626' }}>{kpis.rejected}</span>
            <span className="jdb-kpi-label">Rejected</span>
          </div>
        </div>
        <div className="jdb-kpi-card">
          <div className="jdb-kpi-icon" style={{ background: '#EDE9FE', color: '#7C3AED' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          </div>
          <div className="jdb-kpi-body">
            <span className="jdb-kpi-value">{kpis.inOffer}</span>
            <span className="jdb-kpi-label">In Offer Stage</span>
          </div>
        </div>
        <div className="jdb-kpi-card">
          <div className="jdb-kpi-icon" style={{ background: '#FFF7ED', color: '#EA580C' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/></svg>
          </div>
          <div className="jdb-kpi-body">
            <span className="jdb-kpi-value">{kpis.conversionRate}%</span>
            <span className="jdb-kpi-label">Conversion Rate</span>
          </div>
        </div>
        <div className="jdb-kpi-card">
          <div className="jdb-kpi-icon" style={{ background: '#F0F9FF', color: '#0284C7' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
          </div>
          <div className="jdb-kpi-body">
            <span className="jdb-kpi-value">{kpis.scheduledInterviews}</span>
            <span className="jdb-kpi-label">Interviews Scheduled</span>
          </div>
        </div>
      </div>

      {/* HIRING PROGRESS */}
      <div className="jdb-section-card">
        <h3 className="jdb-section-title">Hiring Progress</h3>
        <div className="jdb-progress-summary">
          <div className="jdb-progress-summary-item">
            <span className="jdb-progress-filled">{kpis.joined}</span>
            <span className="jdb-progress-summary-label">Filled</span>
          </div>
          <div className="jdb-progress-summary-item">
            <span className="jdb-progress-remaining">{kpis.remaining}</span>
            <span className="jdb-progress-summary-label">Remaining</span>
          </div>
          <div className="jdb-progress-summary-item">
            <span className="jdb-progress-pct">{kpis.filledPct}%</span>
            <span className="jdb-progress-summary-label">Complete</span>
          </div>
        </div>
        <div className="jdb-progress-track">
          <div className="jdb-progress-bar" style={{ width: `${kpis.filledPct}%`, background: 'linear-gradient(135deg, #1E40AF, #3B82F6)' }} />
        </div>
        <div className="jdb-progress-labels">
          <span>{kpis.joined} of {kpis.positions} positions filled</span>
          <span>{kpis.filledPct}% complete</span>
        </div>
      </div>

      {/* PROGRESS ANALYTICS OVER TIME */}
      {donutData && (
        <div className="jdb-section-card">
          <h3 className="jdb-section-title" style={{ marginBottom: 16 }}>Progress Analytics Over Time</h3>
          <div className="jdb-chart-grid">
            <DonutChart title="Candidates Added" data={donutData.candidatesAdded} segments={DONUT_SEGMENTS.candidatesAdded} />
            <DonutChart title="Screening Progress" data={donutData.screeningProgress} segments={DONUT_SEGMENTS.screeningProgress} />
            <DonutChart title="Interviews" data={donutData.interviews} segments={DONUT_SEGMENTS.interviews} />
            <DonutChart title="Joining Progress" data={donutData.joiningProgress} segments={DONUT_SEGMENTS.joiningProgress} />
          </div>
        </div>
      )}

      {!donutData && (
        <div className="jdb-section-card">
          <h3 className="jdb-section-title" style={{ marginBottom: 8 }}>Progress Analytics Over Time</h3>
          <div className="jdb-empty-state">
            <div className="jdb-empty-state-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19h16"/><path d="M7 16V9"/><path d="M12 16V5"/><path d="M17 16v-3"/></svg>
            </div>
            <p className="jdb-empty-state-text">Select a date range to view progress charts</p>
          </div>
        </div>
      )}

      {/* RECRUITER PERFORMANCE */}
      {recruiterBreakdown.length > 0 && (
        <div className="jdb-section-card">
          <div className="jdb-section-header">
            <h3 className="jdb-section-title">Recruiter Performance</h3>
            <span className="jdb-section-sub">{recruiterBreakdown.length} recruiters</span>
          </div>
          <div className="jdb-recruiter-table">
            <table>
              <thead>
                <tr>
                  <th scope="col">Recruiter</th>
                  <th scope="col">Total</th>
                  <th scope="col">Active</th>
                  <th scope="col">Joined</th>
                  <th scope="col">Rejected</th>
                </tr>
              </thead>
              <tbody>
                {recruiterBreakdown.map(r => (
                  <tr key={r.name}>
                    <td className="jdb-recruiter-name">{r.name}</td>
                    <td>{r.total}</td>
                    <td>{r.active}</td>
                    <td style={{ color: '#059669', fontWeight: 600 }}>{r.joined}</td>
                    <td style={{ color: '#DC2626' }}>{r.rejected}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* DIMENSION ANALYTICS */}
      <div className="jdb-section-card">
        <h3 className="jdb-section-title" style={{ marginBottom: 20 }}>Dimension Analytics</h3>

        {/* OVERALL JOBS SUMMARY */}
        <div className="jdb-dim-subsection">
          <h4 className="jdb-dim-subtitle">
            <span className="jdb-dim-subtitle-icon" style={{ background: '#EFF6FF', color: '#1E40AF' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
            </span>
            Overall Jobs Summary
          </h4>
          <div className="jdb-dim-grid">
            <div className="jdb-dim-card">
              <div className="jdb-dim-card-header">
                <span className="jdb-dim-card-title">All Jobs</span>
                <span className="jdb-dim-card-badge">{filteredJobs.length}</span>
              </div>
              <div className="jdb-dim-stat-row">
                <span className="jdb-dim-stat-label">Total Candidates</span>
                <span className="jdb-dim-stat-value">{kpis.total}</span>
              </div>
              <div className="jdb-dim-stat-row">
                <span className="jdb-dim-stat-label">Conversion Rate</span>
                <span className="jdb-dim-stat-value">{kpis.conversionRate}%</span>
              </div>
              <div className="jdb-dim-stat-row">
                <span className="jdb-dim-stat-label">Offer Accept Rate</span>
                <span className="jdb-dim-stat-value">{kpis.offerAcceptRate}%</span>
              </div>
            </div>
            <div className="jdb-dim-card">
              <div className="jdb-dim-card-header">
                <span className="jdb-dim-card-title">Positions</span>
                <span className="jdb-dim-card-badge">{totalPositions}</span>
              </div>
              <div className="jdb-dim-stat-row">
                <span className="jdb-dim-stat-label">Filled</span>
                <span className="jdb-dim-stat-value">{kpis.joined}</span>
              </div>
              <div className="jdb-dim-stat-row">
                <span className="jdb-dim-stat-label">Remaining</span>
                <span className="jdb-dim-stat-value">{kpis.remaining}</span>
              </div>
              <div className="jdb-dim-stat-row">
                <span className="jdb-dim-stat-label">Fill Rate</span>
                <span className="jdb-dim-stat-value">{kpis.filledPct}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* DEPARTMENT-LEVEL ANALYTICS (by sub-department / candidate.department) */}
        <div className="jdb-dim-subsection">
          <h4 className="jdb-dim-subtitle">
            <span className="jdb-dim-subtitle-icon" style={{ background: '#F0FDF4', color: '#16A34A' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            </span>
            Candidate Department-Level Analytics
          </h4>
          <div className="jdb-status-legend">
            <span className="jdb-legend-item"><span className="jdb-legend-dot" style={{ background: '#10B981' }} />Joined</span>
            <span className="jdb-legend-item"><span className="jdb-legend-dot" style={{ background: '#3B82F6' }} />Ongoing</span>
            <span className="jdb-legend-item"><span className="jdb-legend-dot" style={{ background: '#EF4444' }} />Rejected</span>
          </div>
          {subDepartmentBreakdown.length > 0 ? (
            <div className="jdb-dim-chart">
              {subDepartmentBreakdown.map(d => {
                const convRate = d.total > 0 ? Math.round((d.joined / d.total) * 100) : 0;
                return (
                  <div key={d.name} className="jdb-dim-chart-row">
                    <div className="jdb-dim-chart-label">
                      <span className="jdb-dim-chart-name">{d.name}</span>
                      <span className="jdb-dim-chart-meta">{d.total} candidates · {convRate}% converted</span>
                    </div>
                    <div className="jdb-dim-chart-track">
                      <div className="jdb-dim-chart-stacked">
                        {d.joined > 0 && <div style={{ width: `${(d.joined / d.total) * 100}%`, background: '#10B981' }} title={`Joined: ${d.joined}`} />}
                        {d.ongoing > 0 && <div style={{ width: `${(d.ongoing / d.total) * 100}%`, background: '#3B82F6' }} title={`Ongoing: ${d.ongoing}`} />}
                        {d.rejected > 0 && <div style={{ width: `${(d.rejected / d.total) * 100}%`, background: '#EF4444' }} title={`Rejected: ${d.rejected}`} />}
                      </div>
                    </div>
                    <span className="jdb-dim-chart-count">{d.total}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="jdb-empty-state jdb-empty-state-sm">
              <p className="jdb-empty-state-text">No department data available for the selected filters</p>
            </div>
          )}
        </div>

        {/* LOCATION ANALYTICS */}
        <div className="jdb-dim-subsection">
          <h4 className="jdb-dim-subtitle">
            <span className="jdb-dim-subtitle-icon" style={{ background: '#FFF7ED', color: '#EA580C' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            </span>
            Job Location Analytics
          </h4>
          {locationBreakdown.length > 0 ? (
            <div className="jdb-dim-chart">
              {locationBreakdown.map(d => {
                const convRate = d.total > 0 ? Math.round((d.joined / d.total) * 100) : 0;
                return (
                  <div key={d.name} className="jdb-dim-chart-row">
                    <div className="jdb-dim-chart-label">
                      <span className="jdb-dim-chart-name">{d.name}</span>
                      <span className="jdb-dim-chart-meta">{d.total} candidates · {convRate}% converted</span>
                    </div>
                    <div className="jdb-dim-chart-track">
                      <div
                        className="jdb-dim-chart-fill"
                        style={{ width: `${maxLocationCount > 0 ? (d.total / maxLocationCount) * 100 : 0}%`, background: '#EA580C' }}
                      />
                    </div>
                    <span className="jdb-dim-chart-count">{d.total}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="jdb-empty-state jdb-empty-state-sm">
              <p className="jdb-empty-state-text">No location data available for the selected filters</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DonutChart({ title, data, segments }) {
  const total = segments.reduce((sum, segment) => sum + (data[segment.key] || 0), 0);
  let offset = 0;
  const gradient = total > 0 ? segments.map(segment => {
    const percent = ((data[segment.key] || 0) / total) * 100;
    const stop = `${segment.color} ${offset}% ${offset + percent}%`;
    offset += percent;
    return stop;
  }).join(', ') : '#E2E8F0 0 100%';

  return (
    <div className="jdb-chart-card jdb-donut-card">
      <div className="jdb-chart-card-header">
        <span className="jdb-chart-card-title">{title}</span>
      </div>
      <div className="jdb-donut-content">
        <div className="jdb-donut" style={{ background: `conic-gradient(${gradient})` }} role="img" aria-label={`${title}: ${total} total`}>
          <div className="jdb-donut-center"><strong>{total}</strong><span>Total</span></div>
        </div>
        <div className="jdb-donut-legend">
          {segments.map(segment => (
            <div key={segment.key} className="jdb-donut-legend-item">
              <span className="jdb-chart-legend-dot" style={{ background: segment.color }} />
              <span>{segment.label}</span><strong>{data[segment.key] || 0}</strong>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const page = {
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontSize: '14px',
  color: '#0F172A',
  animation: 'fadeInUp 0.4s ease-out',
};

const css = `
/* ===== HEADER ===== */
.jdb-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}
.jdb-title {
  font-size: 22px;
  font-weight: 700;
  margin: 0;
  color: #0F172A;
}
.jdb-subtitle {
  font-size: 13px;
  color: #64748B;
  margin: 2px 0 0 0;
}
.jdb-header-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.jdb-status-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.jdb-status-open { background: #DCFCE7; color: #16A34A; }
.jdb-status-closed { background: #FEE2E2; color: #DC2626; }
.jdb-status-hold { background: #FEF9C3; color: #CA8A04; }
.jdb-status-filled { background: #DBEAFE; color: #1E40AF; }
.jdb-meta-chip {
  display: inline-block;
  font-size: 12px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 6px;
  background: #F1F5F9;
  color: #475569;
}

/* ===== JOB INFO ROW ===== */
.jdb-info-row {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  align-items: stretch;
  gap: 12px;
  margin-bottom: 20px;
  padding: 14px 18px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #E8EDF4;
}
.jdb-info-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 0;
  min-height: 42px;
  text-align: center;
}
.jdb-info-label {
  font-size: 10px;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  white-space: nowrap;
}
.jdb-info-val {
  font-size: 13px;
  font-weight: 600;
  color: #0F172A;
  white-space: nowrap;
}

/* ===== FILTER BAR ===== */
.jdb-filter-bar {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #E8EDF4;
  padding: 14px 18px;
  margin-bottom: 20px;
}
.jdb-filter-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}
.jdb-filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.jdb-filter-label {
  font-size: 10px;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.jdb-preset-pills {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}
.jdb-pill {
  padding: 5px 12px;
  border: 1px solid #E2E8F0;
  border-radius: 20px;
  background: #fff;
  color: #475569;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  font-family: 'Inter', sans-serif;
  white-space: nowrap;
}
.jdb-pill:hover {
  background: #F1F5F9;
  border-color: #CBD5E1;
}
.jdb-pill-active {
  background: #1E40AF !important;
  color: #fff !important;
  border-color: #1E40AF !important;
  font-weight: 600;
}
.jdb-input,
.jdb-select {
  padding: 6px 10px;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  font-size: 12px;
  font-family: 'Inter', sans-serif;
  color: #0F172A;
  background: #fff;
  min-width: 130px;
}
.jdb-input:focus,
.jdb-select:focus {
  outline: none;
  border-color: #1E40AF;
  box-shadow: 0 0 0 2px rgba(30, 64, 175, 0.1);
}
.jdb-date-range-pair {
  flex-direction: row;
  gap: 8px;
}
.jdb-date-range-pair > div {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.jdb-reset-btn {
  padding: 6px 14px;
  border: 1px solid #DC2626;
  border-radius: 8px;
  background: #FEF2F2;
  color: #DC2626;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  font-family: 'Inter', sans-serif;
  white-space: nowrap;
  align-self: flex-end;
}
.jdb-reset-btn:hover {
  background: #FEE2E2;
}

/* ===== KPI GRID ===== */
.jdb-kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}
.jdb-kpi-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #E8EDF4;
  transition: box-shadow 0.15s;
}
.jdb-kpi-card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.06);
}
.jdb-kpi-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.jdb-kpi-body {
  display: flex;
  flex-direction: column;
}
.jdb-kpi-value {
  font-size: 22px;
  font-weight: 700;
  color: #0F172A;
  line-height: 1.1;
}
.jdb-kpi-label {
  font-size: 11px;
  color: #64748B;
  font-weight: 500;
  margin-top: 2px;
}

/* ===== SECTION CARDS ===== */
.jdb-section-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #E8EDF4;
  padding: 18px 20px;
  margin-bottom: 20px;
}
.jdb-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.jdb-section-title {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  color: #0F172A;
}
.jdb-section-header .jdb-section-title {
  margin: 0;
}
.jdb-section-sub {
  font-size: 12px;
  color: #94A3B8;
  font-weight: 500;
}

/* ===== HIRING PROGRESS ===== */
.jdb-progress-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
  gap: 16px;
}
.jdb-progress-summary-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}
.jdb-progress-summary-label {
  font-size: 11px;
  color: #94A3B8;
  font-weight: 500;
}
.jdb-progress-filled {
  font-size: 22px;
  font-weight: 700;
  color: #10B981;
}
.jdb-progress-remaining {
  font-size: 22px;
  font-weight: 700;
  color: #94A3B8;
}
.jdb-progress-pct {
  font-size: 22px;
  font-weight: 700;
  color: #0F172A;
}
.jdb-progress-track {
  width: 100%;
  height: 10px;
  background: #F1F5F9;
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 8px;
}
.jdb-progress-bar {
  height: 100%;
  border-radius: 5px;
  transition: width 0.6s ease;
  min-width: 0;
}
.jdb-progress-labels {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #64748B;
}

/* ===== PROGRESS CHARTS ===== */
.jdb-chart-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}
.jdb-chart-card {
  background: #F8FAFC;
  border-radius: 10px;
  border: 1px solid #E8EDF4;
  padding: 14px;
  display: flex;
  flex-direction: column;
  min-height: 300px;
}
.jdb-chart-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.jdb-chart-card-title {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
}
.jdb-chart-card-total {
  font-size: 16px;
  font-weight: 700;
  color: #0F172A;
}
.jdb-donut-card {
  min-height: 230px;
}
.jdb-donut-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 22px;
  flex: 1;
}
.jdb-donut {
  width: 142px;
  height: 142px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
}
.jdb-donut-center {
  width: 92px;
  height: 92px;
  border-radius: 50%;
  background: #F8FAFC;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.jdb-donut-center strong {
  color: #0F172A;
  font-size: 24px;
  line-height: 1;
}
.jdb-donut-center span {
  color: #64748B;
  font-size: 10px;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: .35px;
}
.jdb-donut-legend {
  display: flex;
  flex-direction: column;
  gap: 9px;
  min-width: 115px;
}
.jdb-donut-legend-item {
  display: grid;
  grid-template-columns: 8px 1fr auto;
  align-items: center;
  gap: 6px;
  color: #2d3433;
  font-size: 11px;
}
.jdb-donut-legend-item strong {
  color: #25292e;
  font-size: 12px;
}
.jdb-chart-body {
  display: flex;
  gap: 6px;
  flex: 1;
  min-height: 0;
}
.jdb-chart-axis-y {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  padding: 0 4px 18px 0;
  min-width: 32px;
  flex-shrink: 0;
}
.jdb-chart-axis-label {
  font-size: 8px;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  margin-bottom: 4px;
}
.jdb-chart-y-tick {
  font-size: 9px;
  font-weight: 500;
  color: #94A3B8;
  line-height: 1;
}
.jdb-chart-area {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}
.jdb-chart-scroll {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #CBD5E1 transparent;
}
.jdb-chart-scroll::-webkit-scrollbar {
  height: 4px;
}
.jdb-chart-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.jdb-chart-scroll::-webkit-scrollbar-thumb {
  background: #CBD5E1;
  border-radius: 2px;
}
.jdb-chart-bars {
  display: flex;
  align-items: flex-end;
  height: 160px;
  position: relative;
  min-width: 100%;
}
.jdb-chart-grid-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: #E8EDF4;
  pointer-events: none;
  z-index: 0;
}
.jdb-line-chart {
  height: 184px;
  min-width: 100%;
  position: relative;
  padding-bottom: 24px;
  box-sizing: border-box;
}
.jdb-line-chart-svg {
  width: 100%;
  height: 160px;
  display: block;
  position: relative;
  z-index: 1;
  overflow: visible;
}
.jdb-line-chart-path {
  fill: none;
  stroke: #2563EB;
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.jdb-line-chart-point {
  fill: #fff;
  stroke: #2563EB;
  stroke-width: 2.5;
}
.jdb-line-chart-value {
  position: absolute;
  z-index: 2;
  transform: translate(-50%, -7px);
  color: #334155;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;
}
.jdb-line-chart-labels {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(56px, 1fr);
  align-items: center;
  text-align: center;
  font-size: 10px;
  color: #64748B;
  white-space: nowrap;
}
.jdb-chart-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  z-index: 1;
  flex-shrink: 0;
}
.jdb-chart-bar-wrap {
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  position: relative;
}
.jdb-chart-bar-val {
  font-size: 10px;
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
  pointer-events: none;
  margin-bottom: 2px;
  text-align: center;
}
.jdb-chart-bar-stacked {
  width: 100%;
  max-width: 28px;
  display: flex;
  flex-direction: column;
  border-radius: 3px 3px 0 0;
  overflow: hidden;
  transition: height 0.3s ease;
  cursor: default;
}
.jdb-chart-bar-stacked:hover {
  opacity: 0.85;
  box-shadow: 0 0 0 2px rgba(15, 23, 42, 0.1);
}
.jdb-bar-seg {
  min-height: 1px;
  transition: flex 0.3s ease;
}
.jdb-chart-bar-lbl {
  font-size: 10px;
  color: #64748B;
  white-space: nowrap;
  text-align: center;
  margin-top: 4px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.jdb-chart-axis-x-label {
  font-size: 8px;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;
  margin-top: 2px;
}
.jdb-chart-empty {
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #94A3B8;
}
.jdb-chart-legend {
  display: flex;
  gap: 14px;
  margin-bottom: 8px;
}
.jdb-chart-legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #64748B;
  font-weight: 500;
}
.jdb-chart-legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* ===== STATUS LEGEND ===== */
.jdb-status-legend {
  display: flex;
  gap: 16px;
  margin-bottom: 14px;
}
.jdb-legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #64748B;
  font-weight: 500;
}
.jdb-legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.jdb-dim-chart-stacked {
  display: flex;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
}
.jdb-dim-chart-stacked > div {
  height: 100%;
  min-width: 2px;
  transition: width 0.3s ease;
}

/* ===== PIPELINE BARS ===== */
.jdb-pipeline-bars {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.jdb-pipeline-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.jdb-pipeline-label {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 130px;
}
.jdb-pipeline-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.jdb-pipeline-name {
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
}
.jdb-pipeline-track {
  flex: 1;
  height: 8px;
  background: #F1F5F9;
  border-radius: 4px;
  overflow: hidden;
}
.jdb-pipeline-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.5s ease;
  min-width: 0;
}
.jdb-pipeline-counts {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 70px;
  justify-content: flex-end;
}
.jdb-pipeline-num {
  font-size: 14px;
  font-weight: 700;
  color: #0F172A;
}
.jdb-pipeline-pct {
  font-size: 11px;
  color: #94A3B8;
  min-width: 30px;
  text-align: right;
}

/* ===== STATUS BREAKDOWN ===== */
.jdb-status-grid {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.jdb-status-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.jdb-status-zero {
  opacity: 0.4;
}
.jdb-status-bar-bg {
  width: 100%;
  height: 6px;
  background: #F1F5F9;
  border-radius: 3px;
  overflow: hidden;
}
.jdb-status-bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.4s ease;
}
.jdb-status-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.jdb-status-name {
  font-size: 12px;
  color: #475569;
  text-transform: capitalize;
}
.jdb-status-count {
  font-size: 12px;
  font-weight: 600;
  color: #0F172A;
}

/* ===== INTERVIEW CHIPS ===== */
.jdb-interview-chips {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.jdb-interview-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 12px 20px;
  border-radius: 10px;
  border: 1px solid #E8EDF4;
  background: #F8FAFC;
  min-width: 80px;
}
.jdb-interview-chip-count {
  font-size: 20px;
  font-weight: 700;
  color: #0F172A;
}
.jdb-interview-chip-label {
  font-size: 11px;
  color: #64748B;
  font-weight: 500;
  text-transform: capitalize;
}

/* ===== RECRUITER TABLE ===== */
.jdb-recruiter-table {
  overflow-x: auto;
}
.jdb-recruiter-table table {
  width: 100%;
  border-collapse: collapse;
}
.jdb-recruiter-table th {
  padding: 8px 12px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  color: #64748B;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: #F8FAFC;
  border-bottom: 1px solid #E8EDF4;
}
.jdb-recruiter-table td {
  padding: 10px 12px;
  font-size: 13px;
  border-bottom: 1px solid #F1F5F9;
  color: #334155;
}
.jdb-recruiter-name {
  font-weight: 600;
  color: #0F172A;
}

/* ===== DIMENSION ANALYTICS ===== */
.jdb-dim-subsection {
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #F1F5F9;
}
.jdb-dim-subsection:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}
.jdb-dim-subtitle {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin: 0 0 14px 0;
}
.jdb-dim-subtitle-icon {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.jdb-dim-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.jdb-dim-card {
  background: #F8FAFC;
  border-radius: 10px;
  border: 1px solid #E8EDF4;
  padding: 14px;
}
.jdb-dim-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.jdb-dim-card-title {
  font-size: 13px;
  font-weight: 600;
  color: #0F172A;
}
.jdb-dim-card-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 6px;
  background: #E0E7FF;
  color: #3730A3;
}
.jdb-dim-stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
  border-bottom: 1px solid #F1F5F9;
}
.jdb-dim-stat-row:last-child {
  border-bottom: none;
}
.jdb-dim-stat-label {
  font-size: 12px;
  color: #64748B;
}
.jdb-dim-stat-value {
  font-size: 12px;
  font-weight: 600;
  color: #0F172A;
}

/* ===== DIMENSION CHARTS ===== */
.jdb-dim-chart {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.jdb-dim-chart-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.jdb-dim-chart-label {
  min-width: 180px;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.jdb-dim-chart-name {
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.jdb-dim-chart-meta {
  font-size: 11px;
  color: #94A3B8;
}
.jdb-dim-chart-track {
  flex: 1;
  height: 8px;
  background: #F1F5F9;
  border-radius: 4px;
  overflow: hidden;
}
.jdb-dim-chart-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.4s ease;
}
.jdb-dim-chart-count {
  font-size: 13px;
  font-weight: 700;
  color: #0F172A;
  min-width: 30px;
  text-align: right;
}

/* ===== EMPTY STATES ===== */
.jdb-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  gap: 8px;
}
.jdb-empty-state-sm {
  padding: 20px 16px;
}
.jdb-empty-state-icon {
  color: #CBD5E1;
  margin-bottom: 4px;
}
.jdb-empty-state-text {
  font-size: 13px;
  color: #64748B;
  font-weight: 500;
  margin: 0;
  text-align: center;
}
.jdb-empty-state-sub {
  font-size: 12px;
  color: #94A3B8;
  margin: 0;
  text-align: center;
  max-width: 360px;
  line-height: 1.5;
}
.jdb-empty-state-sub code {
  background: #F1F5F9;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 11px;
  color: #475569;
}
.jdb-empty-state-notice {
  background: #FFFBEB;
  border: 1px solid #FDE68A;
  border-radius: 10px;
  padding: 20px;
}
.jdb-empty-state-notice .jdb-empty-state-icon {
  color: #D97706;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 1024px) {
  .jdb-kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .jdb-chart-grid {
    grid-template-columns: 1fr;
  }
  .jdb-dim-grid {
    grid-template-columns: 1fr;
  }
  .jdb-info-row {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (max-width: 768px) {
  .jdb-info-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .jdb-filter-row {
    flex-direction: column;
    align-items: stretch;
  }
  .jdb-pill {
    font-size: 11px;
    padding: 4px 10px;
  }
  .jdb-pipeline-label {
    min-width: 100px;
  }
  .jdb-dim-chart-label {
    min-width: 120px;
  }
  .jdb-dim-chart-row {
    flex-wrap: wrap;
  }
}
@media (max-width: 480px) {
  .jdb-kpi-grid {
    grid-template-columns: 1fr;
  }
  .jdb-header {
    flex-direction: column;
  }
  .jdb-info-row {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  .jdb-dim-grid {
    grid-template-columns: 1fr;
  }
  .jdb-dim-chart-label {
    min-width: 100%;
  }
  .jdb-donut-content {
    gap: 14px;
  }
  .jdb-donut {
    width: 124px;
    height: 124px;
  }
  .jdb-donut-center {
    width: 80px;
    height: 80px;
  }
  .jdb-donut-legend {
    min-width: 100px;
  }
}
`;
